import React, { useEffect, useMemo, useState } from "react";

const formatDate = (value) => {
  if (!value) return "—";
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(value));
  } catch (err) {
    return value;
  }
};

const statusClass = (status) => {
  const s = String(status || '').toLowerCase();
  if (s === 'completed' || s === 'complete') return 'bw-chip bw-chip-completed';
  if (s === 'failed' || s === 'cancelled' || s === 'canceled') return 'bw-chip bw-chip-cancelled';
  if (s === 'matched') return 'bw-chip bw-chip-matched';
  return 'bw-chip bw-chip-default';
};

const titleCase = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s;

const ActiveRideCard = ({ ride, onCancel, onViewDetails, onMarkCompleted }) => {
  if (!ride) return null;
  const s = String(ride.status || '').toLowerCase();
  const canComplete = s === 'started';
  return (
    <section className="bw-card p-4 border-yellow-500/20">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-white">Active Ride</h2>
        <span className={statusClass(s)}>{titleCase(s)}</span>
      </div>
      <div className="space-y-1 text-sm text-white/90">
        <p><span className="font-medium text-white/80">Pickup:</span> <span className="italic">{ride.pickupLocationName}</span></p>
        <p><span className="font-medium text-white/80">Drop:</span> <span className="italic">{ride.dropLocationName}</span></p>
        <p><span className="font-medium text-white/80">Time:</span> <span className="italic">{formatDate(ride.timeOfRide || ride.createdAt)}</span></p>
        {typeof ride.bid === 'number' && <p><span className="font-medium text-white/80">Bid:</span> <span className="italic">${ride.bid.toFixed(2)}</span></p>}
      </div>
      <div className="mt-3 flex items-center gap-2">
        <button type="button" onClick={() => onCancel?.(ride)} className="bw-btn-ghost">Cancel Ride</button>
        <button type="button" onClick={() => onViewDetails?.(ride)} className="bw-btn">View Ride Details</button>
        {canComplete && (
          <button type="button" onClick={() => onMarkCompleted?.(ride)} className="bw-btn-ghost">Mark Completed</button>
        )}
      </div>
    </section>
  );
};

const RideHistory = ({ rides = [], loading, refreshing, onEditRide, onViewBids, pageSize = 10, activeRide, onCancelActive, onViewActiveDetails, onCompleteActive }) => {
  const [currentPage, setCurrentPage] = useState(1);

  // Reset to first page when rides or pageSize change
  useEffect(() => { setCurrentPage(1); }, [rides, pageSize]);

  const totalItems = rides.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalItems);
  const pageItems = useMemo(() => rides.slice(startIndex, endIndex), [rides, startIndex, endIndex]);

  if (loading && !rides.length) {
    return (
      <section className="bw-card p-4">
        <p className="text-sm bw-muted">Loading ride history...</p>
      </section>
    );
  }

  return (
    <section className="space-y-4">
      <ActiveRideCard
        ride={activeRide}
        onCancel={onCancelActive}
        onViewDetails={onViewActiveDetails}
        onMarkCompleted={onCompleteActive}
      />

      <section className="bw-card p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">Ride History</h2>
          {refreshing && <span className="text-xs bw-muted italic">Updating…</span>}
        </div>

        {rides.length === 0 ? (
          <p className="text-sm bw-muted">No recent rides found.</p>
        ) : (
          <>
            <div className="space-y-3">
              {pageItems.map((ride) => {
                const s = String(ride.status || '').toLowerCase();
                return (
                  <article
                    key={ride.id}
                    className="rounded-lg border border-white/10 bg-black p-4 shadow-sm"
                  >
                    <div className="flex items-center justify-between text-sm">
                      <span className={statusClass(s)}>{titleCase(s)}</span>
                      <span className="bw-muted">{formatDate(ride.timeOfRide || ride.createdAt)}</span>
                    </div>
                    <div className="mt-3 space-y-1 text-sm text-white/90">
                      <p>
                        <span className="font-medium text-white/80">From:</span> <span className="italic">{ride.pickupLocationName}</span>
                      </p>
                      <p>
                        <span className="font-medium text-white/80">To:</span> <span className="italic">{ride.dropLocationName}</span>
                      </p>
                      <p>
                        <span className="font-medium text-white/80">Bid:</span>{" "}
                        <span className="italic">{typeof ride.bid === "number" ? `$${ride.bid.toFixed(2)}` : "—"}</span>
                      </p>
                    </div>

                    {s === 'open' && (
                      <div className="mt-3 flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => onEditRide?.(ride)}
                          className="bw-btn-ghost"
                        >
                          Edit Ride
                        </button>
                        <button
                          type="button"
                          onClick={() => onViewBids?.(ride)}
                          className="bw-btn"
                        >
                          View Bids
                        </button>
                      </div>
                    )}
                  </article>
                );
              })}
            </div>

            {totalPages > 1 && (
              <div className="mt-5 flex flex-col items-center gap-3 sm:flex-row sm:justify-between">
                <div className="text-xs bw-muted">
                  Showing <span className="text-white">{startIndex + 1}</span>–<span className="text-white">{endIndex}</span> of <span className="text-white">{totalItems}</span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className="bw-btn-ghost disabled:opacity-50"
                  >
                    First
                  </button>
                  <button
                    type="button"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="bw-btn-ghost disabled:opacity-50"
                  >
                    Prev
                  </button>
                  <div className="px-2 text-xs bw-muted">
                    Page <span className="text-white">{currentPage}</span> / <span className="text-white">{totalPages}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="bw-btn-ghost disabled:opacity-50"
                  >
                    Next
                  </button>
                  <button
                    type="button"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                    className="bw-btn-ghost disabled:opacity-50"
                  >
                    Last
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </section>
    </section>
  );
};

export default RideHistory;
